import { useEffect, useState } from "react";
import { toast } from "sonner";
import { fmtEUR } from "../lib/format";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, Legend } from "recharts";
import { Filter, TrendingUp } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Money } from "../context/PrivacyContext";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const COLORS = ["#2563eb", "#16a34a", "#dc2626", "#f59e0b", "#8b5cf6", "#0891b2", "#ec4899", "#65a30d", "#a16207", "#7c3aed", "#0d9488", "#e11d48"];

function firstOfMonth(offset = 0) {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + offset);
  return d.toISOString().slice(0, 10);
}
function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function Analitica() {
  const [providers, setProviders] = useState([]);
  const [methods, setMethods] = useState([]);
  const [cats, setCats] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ date_from: firstOfMonth(-5), date_to: todayISO(), category: "", provider_id: "", payment_method_id: "", account_id: "" });

  useEffect(() => {
    api.get("/providers").then((r) => setProviders(r.data));
    api.get("/payment-methods").then((r) => setMethods(r.data));
    api.get("/categories?kind=gasto").then((r) => setCats(r.data.filter((c) => !c.parent_id && c.active)));
    api.get("/assets").then((r) => setAccounts(r.data));
  }, []);

  const load = () => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    api.get("/expenses/summary", { params: p }).then((r) => setSummary(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(load, [filters]);

  const catData = (summary?.by_category || []).slice(0, 10);
  const provData = (summary?.by_provider || []).slice(0, 10);
  const methodData = (summary?.by_payment_method || []).slice(0, 10);
  const accountData = (summary?.by_account || []).slice(0, 10);
  const monthsRaw = summary?.by_month || [];
  const topCatsForLine = catData.slice(0, 5).map((c) => c.label);
  const monthChart = monthsRaw.map((m) => {
    const row = { month: m.month, Total: m.total };
    topCatsForLine.forEach((c) => { row[c] = m.by_category[c] || 0; });
    return row;
  });

  return (
    <div className="space-y-6" data-testid="analitica-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Analítica de Gastos</h1>
          <p className="text-sm text-muted-foreground mt-1">Filtra y visualiza dónde va tu dinero.</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="border border-border rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analitica-filters">
        <div>
          <label className="text-xs text-muted-foreground">Desde</label>
          <Input data-testid="filter-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Hasta</label>
          <Input data-testid="filter-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Categoría</label>
          <Select value={filters.category || "all"} onValueChange={(v) => setFilters({ ...filters, category: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Proveedor</label>
          <Select value={filters.provider_id || "all"} onValueChange={(v) => setFilters({ ...filters, provider_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-provider"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Método de pago</label>
          <Select value={filters.payment_method_id || "all"} onValueChange={(v) => setFilters({ ...filters, payment_method_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {methods.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Cuenta origen</label>
          <Select value={filters.account_id || "all"} onValueChange={(v) => setFilters({ ...filters, account_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-account"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Totales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-border rounded-lg p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Total del período</div>
          <Money value={summary?.total || 0} testId="analitica-total" className="block mt-1 text-2xl sm:text-3xl font-semibold tabular" />
        </div>
        <div className="border border-border rounded-lg p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Nº de gastos</div>
          <div className="text-2xl sm:text-3xl font-semibold tabular mt-1">{summary?.count || 0}</div>
        </div>
        <div className="border border-border rounded-lg p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Ticket medio</div>
          <Money value={summary && summary.count ? summary.total / summary.count : 0} className="block mt-1 text-2xl sm:text-3xl font-semibold tabular" />
        </div>
      </div>

      {/* Circular por categoría */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-border rounded-lg p-5">
          <h3 className="font-heading font-bold text-sm uppercase tracking-wider mb-3">Reparto por categoría</h3>
          {catData.length === 0 ? <div className="text-sm text-muted-foreground py-8 text-center">Sin datos</div> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={catData} dataKey="total" nameKey="label" innerRadius={50} outerRadius={95} paddingAngle={2}>
                  {catData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtEUR(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="border border-border rounded-lg p-5">
          <h3 className="font-heading font-bold text-sm uppercase tracking-wider mb-3">Top proveedores</h3>
          {provData.length === 0 ? <div className="text-sm text-muted-foreground py-8 text-center">Sin datos</div> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={provData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis dataKey="label" type="category" tick={{ fontSize: 10 }} width={100} />
                <Tooltip formatter={(v) => fmtEUR(v)} />
                <Bar dataKey="total" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Evolución mensual */}
      <div className="border border-border rounded-lg p-5">
        <h3 className="font-heading font-bold text-sm uppercase tracking-wider mb-3 flex items-center gap-2"><TrendingUp size={14} /> Evolución mensual por categoría (top 5)</h3>
        {monthChart.length === 0 ? <div className="text-sm text-muted-foreground py-8 text-center">Sin datos</div> : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={monthChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmtEUR(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {topCatsForLine.map((c, i) => <Line key={c} type="monotone" dataKey={c} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />)}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Rankings totales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RankingCard title="Totales por método de pago" data={methodData} testId="ranking-methods" />
        <RankingCard title="Totales por cuenta origen" data={accountData} testId="ranking-accounts" />
      </div>
    </div>
  );
}

function RankingCard({ title, data, testId }) {
  const max = Math.max(1, ...data.map((d) => d.total));
  return (
    <div className="border border-border rounded-lg p-5" data-testid={testId}>
      <h3 className="font-heading font-bold text-sm uppercase tracking-wider mb-3">{title}</h3>
      {data.length === 0 ? <div className="text-sm text-muted-foreground py-4 text-center">Sin datos</div> : (
        <div className="space-y-2">
          {data.map((row) => (
            <div key={row.key} className="text-sm">
              <div className="flex items-center justify-between">
                <span className="truncate">{row.label} <span className="text-xs text-muted-foreground">({row.count})</span></span>
                <Money value={row.total} className="font-semibold tabular" />
              </div>
              <div className="h-1.5 bg-secondary rounded-full mt-1 overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: `${(row.total / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
